import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Loader2, UploadCloud, X } from "lucide-react";
import type { DomainImportRow } from "@/lib/csv-utils";
import type { CsvCleanedValue } from "@/lib/import-config";

interface ImportPreviewProps {
  data: DomainImportRow[];
  onUpload: () => void;
  onCancel: () => void;
  isUploading: boolean;
}

export function ImportPreview({
  data,
  onUpload,
  onCancel,
  isUploading,
}: ImportPreviewProps) {
  if (data.length === 0) return null;

  const headers = Object.keys(data[0]);

  return (
    <div className="animate-in space-y-4 fade-in slide-in-from-bottom-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-foreground">
          Preview ({data.length} rows)
        </h3>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel} disabled={isUploading}>
            <X className="mr-2 h-4 w-4" /> Cancel
          </Button>
          <Button onClick={onUpload} disabled={isUploading}>
            {isUploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing...
              </>
            ) : (
              <>
                <UploadCloud className="mr-2 h-4 w-4" /> Upload to Database
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        <div className="max-w-full overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-b border-border bg-muted/50 hover:bg-transparent">
                {headers.map((head) => (
                  <TableHead
                    key={head}
                    className="px-4 py-2 font-semibold whitespace-nowrap"
                  >
                    {head}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.slice(0, 10).map((row, i) => (
                <TableRow key={i} className="hover:bg-muted/40">
                  {Object.values(row).map((cell: CsvCleanedValue, j) => (
                    <TableCell
                      key={j}
                      className="border-r border-border px-4 py-2 text-xs whitespace-nowrap text-foreground/80 last:border-r-0"
                    >
                      {String(cell)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="border-t border-border bg-muted/50 p-2 text-center text-xs text-muted-foreground">
          Showing first 10 rows. Scroll horizontally to see all columns.
        </div>
      </div>
    </div>
  );
}
